import React, { useState, useEffect, useRef } from 'react';
import { api } from '../api/config';

interface ExchangeRates {
  base: string;
  rates: Record<string, number>;
  updated_at: string;
}

const CURRENCY_LABELS: Record<string, string> = {
  USD: '$1',
  EUR: '€1',
  GBP: '£1',
  JPY: '¥100',
  CNY: '¥1',
};

const ExchangeRateIndicator: React.FC = () => {
  const [rates, setRates] = useState<ExchangeRates | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchRates = async () => {
      try {
        const response = await api.get('/exchange-rate', {
          headers: { 'X-Skip-Interceptor': 'true' },
        });
        if (response.data?.success) {
          setRates(response.data.data);
        }
      } catch {
        // 환율 로딩 실패 시 무시
      }
    };

    fetchRates();
    const interval = setInterval(fetchRates, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!rates?.rates?.USD) return null;

  const usdRate = rates.rates.USD;

  return (
    <div className="relative hidden md:block" ref={dropdownRef}>
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="flex items-center gap-1 px-2 py-1 text-xs text-gray-700 hover:text-gray-900 transition-colors rounded hover:bg-gray-50"
        title="Exchange Rates (KRW)"
      >
        <span className="font-semibold tabular-nums text-gray-800">
          $1 = ₩{usdRate.toLocaleString('en-US', { maximumFractionDigits: 0 })}
        </span>
      </button>

      {showDropdown && (
        <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-2 px-3 min-w-[200px] z-50">
          <div className="text-[10px] text-gray-500 mb-1.5 font-medium">
            Foreign Currency to KRW
          </div>
          {Object.entries(rates.rates).map(([currency, value]) => {
            const unit = CURRENCY_LABELS[currency];
            if (!unit) return null;
            return (
              <div
                key={currency}
                className="flex items-center justify-between py-1 text-xs"
              >
                <span className="text-gray-600 font-medium">
                  {currency} {unit}
                </span>
                <span className="font-semibold text-gray-800 tabular-nums">
                  ₩{value.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                </span>
              </div>
            );
          })}
          {rates.updated_at && (
            <div className="text-[9px] text-gray-400 mt-1.5 pt-1.5 border-t border-gray-100">
              {rates.updated_at.split(',').slice(0, 2).join(',')}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ExchangeRateIndicator;
